/**
 * Echo Hunt -- the fake generator (P6, §5).
 *
 * One fake bullet is generated per LLM call, independently: no story, no world, no other bullets and
 * no corpus (§5.1, invariant #4). That independence is the point -- fakes written "together" would
 * cohere with each other while real bullets only cohere by accident, which is an asymmetric-coherence
 * tell (§17).
 *
 * What each fake call receives (see `Prompts/GenerateFake.ts`):
 *  - the CONCEPT SEED(s) -- 1-2 words, drawn from the project's concreteness word list;
 *  - the target EVENT TYPE -- drawn from the real corpus's own distribution (§5.3), so a tell like
 *    "only real bullets contain power use" cannot form;
 *  - the target LENGTH BAND;
 *  - the house-style rules + the cliché blacklist (§4.3, §5.5).
 *
 * It receives nothing else -- in particular NO world bible (§5.1, invariant #4).
 *
 * ## Seeding (§5.2, "2+2 with banned words")
 *
 * The generator is given the seed words but BANNED from using them, only their rough CONCEPTS, and
 * the concept must shape the SITUATION rather than appear as an object. Otherwise "a lantern appears"
 * would be a dead giveaway in a corpus that has no lanterns. Seed leakage is then checked
 * deterministically after generation (`CheckBullet` with the seeds as banned words) and the reply is
 * regenerated on a hit.
 *
 * Concept COUNT is drawn from the corpus's own `oddityCount` distribution (clamped to 1-2), per the
 * §16.1 default: forcing two domains into every event makes fakes more contrived than real cores,
 * while a count that varies with the real distribution gives no count-rule certainty.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The LLM is passed in as an
 * `LLMBackend`, so tests use `MockLLMBackend`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5, §2 (#3, #6), §16.2 P6.
 */
import {CallLLM, LLMBackend, LLMCallCounter} from "./LLM.js";
import {CheckBullet, CheckBulletOptions, CountWords} from "./Normalize.js";
import {
	BulletEventType,
	BulletEventType_values,
	GeneratedBullet,
	GeneratedBulletRejectReason,
} from "./Types.js";
import {
	GenerateFake_JSONSchema,
	GenerateFake_SystemPrompt,
	GenerateFake_UserPrompt,
} from "./Prompts/GenerateFake.js";
import {DEFAULT_SEED_CONCEPT_COUNT_MAX, DEFAULT_SEED_CONCEPT_COUNT_MIN, SampleSeedWords} from "./Wordlist.js";

export {DEFAULT_SEED_CONCEPT_COUNT_MAX, DEFAULT_SEED_CONCEPT_COUNT_MIN};

// distributions
// ==========

/** The distributions a round's fakes are drawn from -- both measured on the REAL corpus, so fakes
 * cannot be separated by a "fakes never do X" rule (§5.3). */
export type FakeGenerationDistributions = {
	/** usable-bullet count per event type (§5.3) */
	byEventType: Partial<Record<BulletEventType, number>>;
	/** usable-bullet count per `oddityCount` value 0..4. DIAGNOSTIC ONLY as of 2026-10-20 -- it no
	 * longer drives the seed-concept count (it clustered at 0-1, pinning fakes to one concept). Kept
	 * for the preserved sampler below and the Settings dev panel readout. */
	byOddityCount: Record<number, number>;
	/** word count of every usable bullet, for the target length band.
	 * The COUNTS are kept rather than just a min/max/mean, because `ComputeLengthBand` is
	 * percentile-based: a single 60-word outlier must not widen the band for everything else. */
	wordCounts: number[];
};

/** What a fake must look like, structurally (§5.3, §5.2). Every field has a sane fallback so a
 * barely-indexed corpus still produces something rather than throwing. */
export type FakeLengthBand = {minWords: number, maxWords: number};

/** The seed the model gets (§5.2). */
export type FakeSeed = {
	/** the seed words; the model must use their CONCEPTS, never the words themselves */
	words: string[];
	/** how many concepts the model may use (the drawn count within the user's range) */
	conceptCount: number;
};

/** A word-count band derived from the corpus, clamped into a usable range. */
export function ComputeLengthBand(wordCounts: number[]): FakeLengthBand {
	const usable = wordCounts.filter(a=>Number.isFinite(a) && a > 0).sort((a, b)=>a - b);
	if (usable.length == 0) return {minWords: 12, maxWords: 30};

	// use a mid-band percentile rather than the absolute min/max: a single 3-word or 60-word outlier
	// in the corpus should not define the target band
	const low = usable[Math.floor(usable.length * .1)] ?? usable[0];
	const high = usable[Math.min(usable.length - 1, Math.floor(usable.length * .9))] ?? usable[usable.length - 1];
	return {minWords: Math.max(6, Math.round(low)), maxWords: Math.max(Math.round(low) + 4, Math.round(high))};
}

/** Builds both distributions from the usable corpus bullets' `(eventType, oddityCount, wordCount)`
 * triples. */
export function BuildFakeDistributions(bullets: {eventType?: BulletEventType|n, oddityCount?: number|n, text: string}[]): FakeGenerationDistributions {
	const byEventType: Partial<Record<BulletEventType, number>> = {};
	const byOddityCount: Record<number, number> = {};
	const wordCounts: number[] = [];

	for (const bullet of bullets) {
		const eventType = bullet.eventType ?? "other";
		byEventType[eventType] = (byEventType[eventType] ?? 0) + 1;

		const oddity = Math.max(0, Math.round(bullet.oddityCount ?? 0));
		byOddityCount[oddity] = (byOddityCount[oddity] ?? 0) + 1;

		wordCounts.push(CountWords(bullet.text));
	}

	return {byEventType, byOddityCount, wordCounts};
}

/** Draws one key from a `{key: weight}` map, proportionally. Returns null when the map is empty or
 * every weight is zero (so the caller can fall back to a uniform choice). */
export function SampleWeighted<T extends string|number>(weights: Partial<Record<T, number>>, rng: () => number): T|null {
	const entries = (Object.entries(weights) as [string, number][]).filter(([, weight])=>Number.isFinite(weight) && weight > 0);
	if (entries.length == 0) return null;

	const total = entries.reduce((sum, [, weight])=>sum + weight, 0);
	let threshold = rng() * total;
	for (const [key, weight] of entries) {
		threshold -= weight;
		if (threshold <= 0) return CoerceKey<T>(key);
	}
	// floating-point tail: return the last entry rather than null
	return CoerceKey<T>(entries[entries.length - 1][0]);
}

function CoerceKey<T extends string|number>(key: string): T {
	// object keys are always strings; event types are strings and oddity counts are numbers
	const asNumber = Number(key);
	return (Number.isInteger(asNumber) ? asNumber : key) as T;
}

/** Draws the target event type from the corpus distribution (§5.3). Falls back to a uniformly random
 * event type when the distribution is empty, so a fresh corpus still yields usable fakes. */
export function SampleEventType(distributions: FakeGenerationDistributions, rng: () => number): BulletEventType {
	const sampled = SampleWeighted<BulletEventType>(distributions.byEventType, rng);
	if (sampled != null) return sampled;
	return BulletEventType_values[Math.min(BulletEventType_values.length - 1, Math.floor(rng() * BulletEventType_values.length))];
}

/** Default range for the seed-concept count per fake (§5.2). Wide enough to be an experiment knob:
 * raise `max` to 3+ to see how forcing more unrelated domains into one short event reads.
 * The values themselves live in `Wordlist.ts`, which owns the settings shape; re-exported above. */

/** How many seed CONCEPTS one fake uses, drawn uniformly from the user's `[min, max]` range. This used
 * to draw from the corpus `oddityCount` distribution, but that measure clusters at 0-1 in practice,
 * pinning every fake to one concept. The old distribution-matched version is kept below, disabled. */
export function SampleConceptCount(options: {min: number, max: number}, rng: () => number): number {
	// the user's range wins; normalise so a swapped/absurd range still yields an integer in bounds
	const low = Math.max(1, Math.round(Math.min(options.min, options.max)));
	const high = Math.max(low, Math.round(Math.max(options.min, options.max)));
	return low + Math.min(high - low, Math.floor(rng() * (high - low + 1)));
}

// oxlint-disable-next-line lf/sc -- preserved old-code area (disabled variant, kept for comparison)
/* DISTRIBUTION-MATCHED VARIANT, disabled 2026-10-20 (see the note above). Kept so the two approaches
 * can be compared once there is enough corpus data to judge whether matching actually helps:
export function SampleConceptCount(distributions: FakeGenerationDistributions, rng: () => number): number {
	const sampled = SampleWeighted<number>(distributions.byOddityCount, rng);
	const raw = sampled == null ? DEFAULT_SEED_CONCEPT_COUNT_MAX : sampled;
	return Math.max(1, Math.min(2, raw));
}
*/

/** Builds one fake's seed: the concept count plus that many DISTINCT words. Distinct matters -- two
 * copies of one word would collapse into a single concept while the prompt still claims two. */
export function SampleFakeSeed(wordlist: string[], conceptCountRange: {min: number, max: number}, rng: () => number): FakeSeed {
	const conceptCount = SampleConceptCount(conceptCountRange, rng);
	return {words: SampleSeedWords(wordlist, conceptCount, rng), conceptCount};
}

// generation
// ==========

export type GenerateFakeOptions = {
	/** the filtered seed-word list (§5.2); see `Wordlist.ts` */
	wordlist: string[];
	/** corpus-measured distributions; see `BuildFakeDistributions` */
	distributions: FakeGenerationDistributions;
	/** how many seed concepts a fake uses, drawn uniformly from this inclusive range (§5.2).
	 * Defaults to 1..2. Raising `max` to 3+ is the experiment the plan-doc's §5.2 note calls for. */
	conceptCountRange?: {min: number, max: number}|n;
	/** `()=>number` in `[0, 1)`; pass a seeded RNG for reproducible tests (§16.2 P6) */
	rng: () => number;
	/** cliché phrases to avoid (§5.5) */
	blacklist?: string[]|n;
	/** the user's banned terms (§4.4) -- also banned from fakes, so a fake cannot smuggle in a term
	 * the corpus excludes */
	bannedWords?: string[]|n;
	/** minimum words per bullet (§4.4); defaults to 6 via `CheckBullet` */
	minWords?: number|n;
	/** model for the `generator` role; "" lets the backend/settings decide */
	model?: string|n;
	/** per-run tally (§13.1); omit for one-off/dev calls */
	counter?: LLMCallCounter|n;
	/** how many candidates to try before giving up on this fake. Default 3 (§16.2 P6). */
	maxAttempts?: number;
	/** Optional REJECTION SAMPLING (§5.5). When set, several clean candidates are produced and the one the
	 * callback rates as MOST HUMAN (i.e. the lowest P(dream-sourced)) is kept. Defaults to off, because it
	 * multiplies the paid calls per fake for a benefit nobody has measured yet. */
	rejectionSampling?: RejectionSamplingOptions|n;
};

/** The rejection-sampling hook (§5.5). `rate` is a judge call over a single candidate, so the callback is
 * injected rather than implemented here -- the pure module must not know which backend or model rates.
 *
 * The callback should score the text with the HELD-OUT judge, not the loop judge: a candidate chosen against
 * the judge that will later judge it is Goodhart'd by construction (§9.5, §17). */
export type RejectionSamplingOptions = {
	/** how many CLEAN candidates to draw before choosing. The extra draws cost one generator call each. */
	candidates: number;
	/** rates one candidate: the smaller the number, the more human it reads */
	rate: (text: string)=>Promise<number>;
};

export type GenerateFakeResult = {
	/** the bullet that survived, or null when every attempt was rejected */
	bullet: GeneratedBullet|null;
	/** every candidate, including the accepted one, in order (they are all logged on the round, §5) */
	candidates: GeneratedBullet[];
	/** the seed that was drawn for this fake */
	seed: FakeSeed;
	eventType: BulletEventType;
	lengthBand: FakeLengthBand;
	/** tokens spent */
	outputTokens: number;
	/** the last rejection reason, when `bullet` is null */
	lastRejectReason?: GeneratedBulletRejectReason|n;
};

/** Generates ONE fake bullet (§5.2, §5.3, §5.5), with bounded retries on rejection. Rejections are
 * recorded per candidate because the Settings tab's dev tool shows them to a human reviewer (§9.5).
 * Seed words are passed to `CheckBullet` as banned words -- the deterministic half of the §5.2 rule. */
export async function GenerateFake(backend: LLMBackend, options: GenerateFakeOptions): Promise<GenerateFakeResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const conceptCountRange = options.conceptCountRange ?? {min: DEFAULT_SEED_CONCEPT_COUNT_MIN, max: DEFAULT_SEED_CONCEPT_COUNT_MAX};
	const seed = SampleFakeSeed(options.wordlist, conceptCountRange, options.rng);
	const eventType = SampleEventType(options.distributions, options.rng);
	const lengthBand = ComputeLengthBand(options.distributions.wordCounts);

	const bannedWords = [...(options.bannedWords ?? []), ...seed.words];
	const checkOptions: CheckBulletOptions = {
		bannedWords,
		blacklist: options.blacklist ?? [],
		minWords: options.minWords ?? undefined,
	};

	const candidates: GeneratedBullet[] = [];
	let outputTokens = 0;
	let lastRejectReason: GeneratedBulletRejectReason|n = null;

	// rejection sampling (§5.5): how many CLEAN candidates this fake still needs before choosing one. 1 when
	// the option is off, so the paths below are the same code either way.
	const sampling = options.rejectionSampling ?? null;
	const wantedCandidates = sampling == null ? 1 : Math.max(1, Math.round(sampling.candidates));
	const cleanCandidates: GeneratedBullet[] = [];

	// a bounded TOTAL number of generator calls: the retry budget plus the extra draws sampling needs, so a
	// model that keeps producing rejected candidates cannot make this loop run forever
	const totalCalls = maxAttempts + (wantedCandidates - 1) * maxAttempts;

	for (let attempt = 0; attempt < totalCalls; attempt++) {
		const result = await CallLLM(backend, {
			role: "generator",
			model: options.model ?? null,
			system: GenerateFake_SystemPrompt(),
			user: GenerateFake_UserPrompt({
				seedWords: seed.words,
				conceptCount: seed.conceptCount,
				eventType,
				lengthBand,
				blacklist: options.blacklist,
				bannedWords: options.bannedWords,
				fixNotes: lastRejectReason != null && candidates.length > 0
					? DescribeRejection(candidates[candidates.length - 1].rejected ?? "retriesExhausted", candidates[candidates.length - 1].text)
					: null,
			}),
			json: GenerateFake_JSONSchema(),
			counter: options.counter ?? null,
		});
		outputTokens += result.outputTokens;

		const text = typeof (result.parsed as {text?: unknown}|n)?.text == "string"
			? ((result.parsed as {text: string}).text).trim()
			: "";

		const candidate: GeneratedBullet = {
			id: `${seed.words.join("-")}#${attempt}`,
			model: result.model,
			seedWords: seed.words,
			seedCategories: null,
			eventType,
			text,
			nearestCorpusSim: null, // the typicality cap is P12
			rejected: null,
		};

		// 1) the model must actually have produced text (an unparseable reply is a rejection, not a crash)
		if (text.length == 0) {
			candidate.rejected = "retriesExhausted";
			lastRejectReason = candidate.rejected;
			candidates.push(candidate);
			continue;
		}

		// 2) the §7 gate, with the seed words counted as banned (§5.2)
		const violations = CheckBullet(text, checkOptions);
		if (violations.length > 0) {
			// a seed leak is worth naming separately from a generic style problem: it is the one
			// rejection reason that says something about the PROMPT rather than about this candidate
			candidate.rejected = violations.some(a=>a.kind == "bannedTerm" && seed.words.some(word=>a.match.toLowerCase().includes(word.toLowerCase())))
				? "seedWordLeak"
				: "styleViolation";
			lastRejectReason = candidate.rejected;
			candidates.push(candidate);
			continue;
		}

		candidates.push(candidate);

		// a clean candidate. With rejection sampling off, the first one wins and we are done.
		if (sampling == null) {
			return {bullet: candidate, candidates, seed, eventType, lengthBand, outputTokens};
		}

		cleanCandidates.push(candidate);
		if (cleanCandidates.length < wantedCandidates) continue;

		// enough candidates: rate them and keep the most human. Every candidate is rated, including the
		// winner, so the log shows what the choice was actually between.
		let best = cleanCandidates[0];
		let bestScore = await sampling.rate(best.text);
		for (const other of cleanCandidates.slice(1)) {
			const score = await sampling.rate(other.text);
			if (score < bestScore) {
				// the one we were about to keep becomes a logged rejection -- §5 invariant #5 wants every
				// generated candidate on the round, not only the survivor
				best.rejected = "resampled";
				best = other;
				bestScore = score;
			} else {
				other.rejected = "resampled";
			}
		}

		return {bullet: best, candidates, seed, eventType, lengthBand, outputTokens};
	}

	// out of calls. If sampling produced at least one clean candidate, that is strictly better than nothing --
	// it just was not compared against a second one.
	if (cleanCandidates.length > 0) {
		const survivor = cleanCandidates[0];
		for (const other of cleanCandidates.slice(1)) other.rejected = "resampled";
		return {bullet: survivor, candidates, seed, eventType, lengthBand, outputTokens};
	}

	return {bullet: null, candidates, seed, eventType, lengthBand, outputTokens, lastRejectReason: lastRejectReason ?? "retriesExhausted"};
}

/** A human-readable account of why a candidate was rejected, fed back into the next attempt's prompt
 * (so the retry is an informed retry rather than a resample). */
export function DescribeRejection(reason: GeneratedBulletRejectReason, text: string): string {
	switch (reason) {
		case "seedWordLeak":
			return `Your previous reply used a seed WORD itself ("${text}"). Use only the CONCEPT behind the seed words -- never the words, and never an obvious stand-in object.`;
		case "styleViolation":
			return `Your previous reply broke the house style or used a banned/cliché phrase ("${text}"). Rewrite it cleanly.`;
		case "tooTypical":
			return `Your previous reply was too close to one of my own remembered events ("${text}"). Pick a different situation.`;
		case "retriesExhausted":
			return `Your previous reply was not usable ("${text}"). Reply with ONLY the JSON object described above, with a non-empty \`text\`.`;
		case "resampled":
			return `Your previous reply was replaced during the judge loop ("${text}").`;
	}
}

// model rotation (§5.5 "rotate models")
// ==========

/** Picks the next fake's model by round-robin. Rotating models is an anti-cliché measure (§5.5): one
 * model has one set of fingerprints, and the user may already know some from LLM adventure play (§17).
 * `index` is the fake's position in the batch, so N fakes cover min(N, models) distinct models. */
export function PickGeneratorModel(models: string[], index: number): string|n {
	const cleaned = models.map(a=>a.trim()).filter(a=>a.length > 0);
	if (cleaned.length == 0) return null;
	return cleaned[((index % cleaned.length) + cleaned.length) % cleaned.length];
}

// batches (§16.2 P6 "Generate 10 fakes")
// ==========

/** One rng is shared by the whole batch on purpose: the draws are sequential and independent, so a single
 * stream keeps a seeded run reproducible end to end. */
export type GenerateFakesOptions = GenerateFakeOptions & {
	/** how many fakes to produce */
	count: number;
	/** model rotation list (§5.5); when empty, `options.model` is used for every call */
	generatorModels?: string[]|n;
};

/** Generates a batch of independent fakes, one call each (§5.1). Sequential on purpose: the calls are
 * independent by contract, and serializing them keeps the token counter and the rotation index
 * meaningful and avoids hammering a rate limit. */
export async function GenerateFakes(backend: LLMBackend, options: GenerateFakesOptions): Promise<GenerateFakeResult[]> {
	const results: GenerateFakeResult[] = [];
	for (let i = 0; i < options.count; i++) {
		const model = PickGeneratorModel(options.generatorModels ?? [], i) ?? options.model ?? null;
		results.push(await GenerateFake(backend, {...options, model}));
	}
	return results;
}
